import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  AppState,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { storage } from '../../utils/storage';
import { AppLayout } from '../../components/layout/AppLayout';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
  sessionId?: string;
}

interface ImportStats {
  totalRecords: number;
  captainIdMatches: number;
  nameMatches: number;
  notFound: number;
  duplicates: number;
  totalAmount: number;
  accuracy: number;
  dateRange?: string;
  batchId?: string;
  sourceFile?: string;
  importType?: string;
  fileHash?: string;
  processedRecords?: number;
  rawRecordsSaved?: number;
}

export const CaremImportScreenFixed = () => {
  const navigation = useNavigation();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 6,
    message: 'Ready to import Careem data',
  });
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const appState = useRef(AppState.currentState);

  // Generate unique session ID for this import session
  useEffect(() => {
    const newSessionId = `careem_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    // Load any previous session state
    loadSessionState(newSessionId);
  }, []);

  // Handle app state changes (background/foreground)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription?.remove();
  }, []);

  const handleAppStateChange = (nextAppState: string) => {
    if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
      // App came to foreground, restore state if processing
      if (isProcessing) {
        restoreProcessingState();
      }
    } else if (nextAppState.match(/inactive|background/) && isProcessing) {
      // App going to background, save state
      saveSessionState();
    }
    appState.current = nextAppState;
  };

  const saveSessionState = async () => {
    try {
      const state = {
        selectedFile,
        progress,
        importStats,
        isProcessing,
        sessionId,
        timestamp: Date.now()
      };
      await storage.setItem(`careem_import_${sessionId}`, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save session state:', error);
    }
  };

  const loadSessionState = async (sid: string) => {
    try {
      const savedState = await storage.getItem(`careem_import_${sid}`);
      if (savedState) {
        const state = JSON.parse(savedState);
        // Only restore if less than 30 minutes old
        if (Date.now() - state.timestamp < 30 * 60 * 1000) {
          setSelectedFile(state.selectedFile);
          setProgress(state.progress);
          setImportStats(state.importStats);
          setIsProcessing(state.isProcessing);
        } else {
          // Clear old state
          await storage.removeItem(`careem_import_${sid}`);
        }
      }
    } catch (error) {
      console.warn('Failed to load session state:', error);
    }
  };

  const restoreProcessingState = async () => {
    try {
      // Check if there's an ongoing import batch in the database
      if (progress.sessionId) {
        const { data: batchStatus } = await supabase
          .from('careem_income_raw')
          .select('batch_id, imported_at')
          .eq('session_id', progress.sessionId)
          .order('imported_at', { ascending: false })
          .limit(1);

        if (batchStatus && batchStatus.length > 0) {
          // Import was completed while in background
          setProgress({
            ...progress,
            phase: 'complete',
            message: 'Import completed while app was in background'
          });
          setIsProcessing(false);
        }
      }
    } catch (error) {
      console.warn('Failed to restore processing state:', error);
    }
  };

  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload CSV file from Careem' },
    { id: 2, name: 'File Validation', description: 'Validate file format and structure' },
    { id: 3, name: 'Duplicate Detection', description: 'Check for duplicate transactions' },
    { id: 4, name: 'Captain Matching', description: 'Match drivers using Captain IDs' },
    { id: 5, name: 'Data Processing', description: 'Process earnings with chunked batches' },
    { id: 6, name: 'Database Update', description: 'Save data to accounting system' },
  ];

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: `Selected: ${result.assets[0].name}`,
          sessionId
        });
        // Clear any previous import stats
        setImportStats(null);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick file');
      console.error('File picker error:', error);
    }
  };

  const performOptimizedImport = async (skipDuplicateCheck = false) => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a file first');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Step 1: File Upload & Reading
      setProgress({ 
        phase: 'uploading', 
        currentStep: 1, 
        totalSteps: 6, 
        message: 'Reading file...', 
        sessionId,
        details: 'Loading CSV content into memory'
      });
      
      let content: string;
      if (selectedFile.uri) {
        const response = await fetch(selectedFile.uri);
        content = await response.text();
      } else {
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(selectedFile as any);
        });
      }
      
      // Step 2: File Validation
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 6, 
        message: 'Validating file format...',
        sessionId,
        details: 'Checking CSV structure and headers'
      });

      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('File appears to be empty or invalid');
      }

      const headers = lines[0].split(',');
      console.log('CSV Headers:', headers);

      // Step 3: Duplicate Detection (if not skipped)
      if (!skipDuplicateCheck) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 3, 
          totalSteps: 6, 
          message: 'Checking for duplicates...',
          sessionId,
          details: 'Verifying against existing data'
        });

        const duplicateResult = await checkForDuplicatesOptimized(lines);
        if (duplicateResult.hasDuplicates) {
          setIsProcessing(false);
          
          if (duplicateResult.isDuplicateFile) {
            Alert.alert(
              'Duplicate File Detected',
              `This file was already imported. Records: ${duplicateResult.duplicateCount}`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Force Re-import', onPress: () => performOptimizedImport(true) }
              ]
            );
            return;
          } else {
            Alert.alert(
              'Potential Duplicates',
              `Found ${duplicateResult.duplicateCount} potential duplicates.`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Continue', onPress: () => processDataChunked(lines) }
              ]
            );
            return;
          }
        }
      }

      await processDataChunked(lines);

    } catch (error) {
      console.error('Import error:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 6,
        message: 'Import failed',
        sessionId,
        details: error.message || 'An unexpected error occurred'
      });
      setIsProcessing(false);
    }
  };

  const checkForDuplicatesOptimized = async (lines: string[]) => {
    try {
      const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
      const fileHash = await calculateFileHash(lines.join('\n'));
      
      // Quick hash-based duplicate check
      const { data: hashCheck } = await supabase
        .from('careem_income_raw')
        .select('batch_id, imported_at')
        .eq('file_hash', fileHash)
        .limit(1);

      if (hashCheck && hashCheck.length > 0) {
        return {
          hasDuplicates: true,
          isDuplicateFile: true,
          duplicateCount: lines.length - 1
        };
      }

      // Basic date range check (faster than complex queries)
      const { data: dateCheck } = await supabase
        .from('accounting_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('category', 'careem')
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate);

      return {
        hasDuplicates: (dateCheck || 0) > 0,
        isDuplicateFile: false,
        duplicateCount: dateCheck || 0
      };
    } catch (error) {
      console.warn('Duplicate check failed:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  const processDataChunked = async (lines: string[]) => {
    // Step 4: Captain Matching
    setProgress({ 
      phase: 'processing', 
      currentStep: 4, 
      totalSteps: 6, 
      message: 'Matching captains...',
      sessionId,
      details: 'Finding drivers in database'
    });

    const batchId = crypto.randomUUID();
    const fileHash = await calculateFileHash(lines.join('\n'));
    const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
    
    let processedCount = 0;
    let totalAmount = 0;
    let idMatches = 0;
    let nameMatches = 0;
    let notFoundCount = 0;

    const chunkSize = 50; // Process 50 records at a time
    const dataLines = lines.slice(1); // Skip header
    const totalRecords = dataLines.length;

    // Step 5: Chunked Data Processing
    setProgress({ 
      phase: 'processing', 
      currentStep: 5, 
      totalSteps: 6, 
      message: 'Processing data in chunks...',
      sessionId,
      details: `Processing ${totalRecords} records in batches of ${chunkSize}`,
      processed: 0,
      total: totalRecords
    });

    for (let i = 0; i < dataLines.length; i += chunkSize) {
      const chunk = dataLines.slice(i, i + chunkSize);
      const chunkResults = await processChunk(chunk, batchId, fileHash, dateRange, i);
      
      processedCount += chunkResults.processed;
      totalAmount += chunkResults.totalAmount;
      idMatches += chunkResults.idMatches;
      nameMatches += chunkResults.nameMatches;
      notFoundCount += chunkResults.notFound;

      // Update progress
      setProgress({ 
        phase: 'processing', 
        currentStep: 5, 
        totalSteps: 6, 
        message: 'Processing data in chunks...',
        sessionId,
        details: `Processed ${Math.min(i + chunkSize, totalRecords)} of ${totalRecords} records`,
        processed: Math.min(i + chunkSize, totalRecords),
        total: totalRecords
      });

      // Save state after each chunk
      await saveSessionState();

      // Small delay to prevent UI blocking
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Step 6: Final Database Update
    setProgress({ 
      phase: 'processing', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Finalizing import...',
      sessionId,
      details: 'Saving final results'
    });

    const stats: ImportStats = {
      totalRecords,
      captainIdMatches: idMatches,
      nameMatches: nameMatches,
      notFound: notFoundCount,
      duplicates: 0,
      totalAmount,
      accuracy: Math.round(((idMatches + nameMatches) / totalRecords) * 100),
      dateRange: `${dateRange.startDate} to ${dateRange.endDate}`,
      batchId: batchId.substring(0, 8),
      sourceFile: selectedFile?.name || 'unknown',
      importType: 'Chunked Processing',
      fileHash: fileHash.substring(0, 16),
      processedRecords: processedCount,
      rawRecordsSaved: totalRecords
    };

    setImportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Import completed successfully!',
      sessionId,
      details: `${idMatches + nameMatches} transactions imported for period ${stats.dateRange}`
    });
    
    setIsProcessing(false);
    
    // Clear session state
    await storage.removeItem(`careem_import_${sessionId}`);
  };

  const processChunk = async (chunk: string[], batchId: string, fileHash: string, dateRange: any, startIndex: number) => {
    const rawRecords: any[] = [];
    const transactions: any[] = [];
    let processed = 0;
    let totalAmount = 0;
    let idMatches = 0;
    let nameMatches = 0;
    let notFound = 0;

    for (let i = 0; i < chunk.length; i++) {
      const line = chunk[i].trim();
      if (!line) continue;

      const cells = line.split(',').map(cell => cell?.trim() || '');
      
      // FIXED: Map ALL CSV fields to database columns
      const captainId = cells[0] || '';
      const captainName = cells[1] || '';
      const phoneNumber = cells[2] || '';
      const vehicleType = cells[3] || '';
      const availableHours = parseFloat(cells[4] || '0');
      const acceptanceRate = parseFloat(cells[5] || '0');
      const totalTrips = parseInt(cells[6] || '0');
      const totalEarnings = parseFloat(cells[7] || '0');
      const cashBalance = parseFloat(cells[8] || '0');

      if (totalEarnings > 0 && captainName !== '') {
        const result = await findCareemDriverOptimized(captainId, captainName);
        const transactionDate = calculateTransactionDate(dateRange.startDate, dateRange.endDate, startIndex + i, chunk.length);
        
        // FIXED: Add complete raw record with ALL fields
        rawRecords.push({
          careem_captain_id: captainId,
          captain_name: captainName,
          driver_name: captainName,
          phone_number: phoneNumber,
          vehicle_type: vehicleType,
          available_hours: availableHours,
          acceptance_rate: acceptanceRate,
          total_trips: totalTrips,
          total_earnings: totalEarnings,
          cash_balance: cashBalance,
          processed_to_accounting: result.driver ? true : false,
          driver_id: result.driver?.id || null,
          match_method: result.matchType || 'not_found',
          import_batch_id: batchId,
          batch_start_date: dateRange.startDate,
          batch_end_date: dateRange.endDate,
          report_week_start: dateRange.startDate,
          report_week_end: dateRange.endDate,
          source_file: selectedFile?.name || 'unknown_file',
          file_hash: fileHash,
          session_id: sessionId,
          raw: JSON.stringify({
            captainId, captainName, phoneNumber, vehicleType,
            availableHours, acceptanceRate, totalTrips, totalEarnings, cashBalance
          })
        });
        
        if (result.driver) {
          if (result.matchType === 'id') idMatches++;
          else if (result.matchType === 'name') nameMatches++;
          
          transactions.push({
            transaction_date: transactionDate,
            type: 'earning',
            category: 'careem_payment',
            amount: totalEarnings,
            description: `Careem earnings: ${captainName} (${totalTrips} trips, ${vehicleType || 'N/A'})`,
            driver_id: result.driver.id,
            import_batch_id: batchId,
            batch_start_date: dateRange.startDate,
            batch_end_date: dateRange.endDate,
            source_filename: selectedFile?.name || 'unknown_file',
            session_id: sessionId
          });

          totalAmount += totalEarnings;
          processed++;
        } else {
          notFound++;
        }
      }
    }

    // Batch insert raw records
    if (rawRecords.length > 0) {
      const { error: rawError } = await supabase
        .from('careem_income_raw')
        .insert(rawRecords);
      
      if (rawError) {
        console.warn('Failed to insert raw records:', rawError);
      }
    }

    // Batch insert transactions
    if (transactions.length > 0) {
      const { error: transError } = await supabase
        .from('accounting_transactions')
        .insert(transactions);
      
      if (transError) {
        console.warn('Failed to insert transactions:', transError);
      }
    }

    return {
      processed,
      totalAmount,
      idMatches,
      nameMatches,
      notFound
    };
  };

  // Optimized driver lookup with caching
  const driverCache = useRef(new Map());
  
  const findCareemDriverOptimized = async (captainId: string, captainName: string) => {
    const cacheKey = `${captainId}_${captainName}`;
    
    if (driverCache.current.has(cacheKey)) {
      return driverCache.current.get(cacheKey);
    }

    try {
      let result = { driver: null, matchType: 'none' };

      // Try Captain ID match first
      if (captainId && captainId.trim() !== '') {
        const { data: driverById } = await supabase
          .from('drivers')
          .select('id, name, careem_captain_id')
          .eq('careem_captain_id', captainId.trim())
          .limit(1);
        
        if (driverById && driverById.length > 0) {
          result = { driver: driverById[0], matchType: 'id' };
        }
      }
      
      // Fallback to name matching
      if (!result.driver && captainName && captainName.trim() !== '') {
        const { data: driversByName } = await supabase
          .from('drivers')
          .select('id, name, careem_captain_id')
          .ilike('name', `%${captainName.trim()}%`)
          .limit(1);
        
        if (driversByName && driversByName.length > 0) {
          result = { driver: driversByName[0], matchType: 'name' };
        }
      }

      // Cache the result
      driverCache.current.set(cacheKey, result);
      return result;
    } catch (error) {
      console.warn(`Error finding driver ${captainName}:`, error);
      const result = { driver: null, matchType: 'error' };
      driverCache.current.set(cacheKey, result);
      return result;
    }
  };

  // Helper functions
  const calculateFileHash = async (content: string): Promise<string> => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(content);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      return 'hash_failed_' + Date.now();
    }
  };

  const extractDateRangeFromFilename = (filename: string) => {
    const datePattern = /(\d{4}-\d{2}-\d{2})/g;
    const dates = filename.match(datePattern);
    
    if (dates && dates.length >= 2) {
      return { startDate: dates[0], endDate: dates[dates.length - 1] };
    }
    
    // Default to current week
    const today = new Date();
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return {
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0]
    };
  };

  const calculateTransactionDate = (startDate: string, endDate: string, recordIndex: number, totalRecords: number) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    const dayOffset = Math.floor((recordIndex / totalRecords) * totalDays);
    const transactionDate = new Date(start);
    transactionDate.setDate(start.getDate() + dayOffset);
    
    return transactionDate.toISOString().split('T')[0];
  };

  const resetImport = () => {
    setSelectedFile(null);
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 6,
      message: 'Ready to import Careem data',
    });
    setImportStats(null);
    setIsProcessing(false);
    driverCache.current.clear();
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber && isProcessing) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#00C851';
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#00C851" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Careem Import (Fixed)" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#00C851' + '15' }]}>
              <Ionicons name="car-sport" size={24} color="#00C851" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Careem Import (Optimized)</Text>
              <Text style={styles.subtitle}>Fixed background processing & chunked imports</Text>
            </View>
          </View>

          {/* Progress Indicator */}
          {isProcessing && (
            <View style={styles.backgroundWarning}>
              <Ionicons name="shield-checkmark" size={20} color="#00C851" />
              <Text style={styles.backgroundText}>
                Safe to minimize app - import will continue in background
              </Text>
            </View>
          )}

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Select File</Text>
            <TouchableOpacity 
              style={[styles.filePickerButton, { borderColor: '#00C851' }]} 
              onPress={pickFile}
              disabled={isProcessing}
            >
              <Ionicons name="cloud-upload" size={20} color="#00C851" />
              <Text style={[styles.filePickerText, { color: '#00C851' }]}>
                {selectedFile ? selectedFile.name : 'Choose Careem CSV file'}
              </Text>
            </TouchableOpacity>
            {selectedFile && (
              <View style={styles.fileInfo}>
                <Ionicons name="document-text" size={16} color={theme.colors.success} />
                <Text style={styles.fileInfoText}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </View>
            )}
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Import Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#00C851' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {isProcessing && (
                    <ActivityIndicator size="small" color="#00C851" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressContainer}>
                    <View style={styles.progressBar}>
                      <View 
                        style={[
                          styles.progressFill, 
                          { 
                            width: `${(progress.processed / progress.total) * 100}%`,
                            backgroundColor: '#00C851'
                          }
                        ]} 
                      />
                    </View>
                    <Text style={styles.progressText}>
                      {progress.processed}/{progress.total} ({Math.round((progress.processed / progress.total) * 100)}%)
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Import Results */}
          {importStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>3. Import Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{importStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#00C851' }]}>
                    {importStats.captainIdMatches}
                  </Text>
                  <Text style={styles.statLabel}>ID Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.warning }]}>
                    {importStats.nameMatches}
                  </Text>
                  <Text style={styles.statLabel}>Name Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                    {importStats.notFound}
                  </Text>
                  <Text style={styles.statLabel}>Not Found</Text>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Amount:</Text>
                  <Text style={styles.summaryValue}>
                    AED {importStats.totalAmount.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Accuracy:</Text>
                  <Text style={[styles.summaryValue, { color: '#00C851' }]}>
                    {importStats.accuracy}%
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Batch ID:</Text>
                  <Text style={styles.summaryValue}>
                    {importStats.batchId}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {!isProcessing ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#00C851' },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(false)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    Import with Duplicate Check
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#FF8800', marginTop: 8 },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(true)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="flash" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Quick Import (Skip Duplicates)</Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#00C851', marginTop: 8 }]} 
                    onPress={resetImport}
                  >
                    <Ionicons name="refresh" size={20} color="#00C851" />
                    <Text style={[styles.secondaryButtonText, { color: '#00C851' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <View style={styles.processingContainer}>
                <TouchableOpacity style={styles.disabledButton} disabled>
                  <ActivityIndicator size="small" color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Processing... (Safe to minimize)</Text>
                </TouchableOpacity>
                <Text style={styles.processingNote}>
                  Import will continue in background. You can safely switch to other apps.
                </Text>
              </View>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  backgroundWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: '#00C851' + '10',
    borderWidth: 1,
    borderColor: '#00C851' + '30',
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.sm,
  },
  backgroundText: {
    fontSize: theme.typography.sizes.sm,
    color: '#00C851',
    fontWeight: '500' as any,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#00C851' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressContainer: {
    gap: theme.spacing.xs,
  },
  progressBar: {
    height: 6,
    backgroundColor: theme.colors.gray200,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  progressText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  processingContainer: {
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  processingNote: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

export default CaremImportScreenFixed;
